import { Link, useParams } from 'react-router-dom';
import { formatDate, formatDateTime, formatGel, formatMultiplier, formatPoints, formatRate } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { useAuthMethods, useCustomer, useCustomerOrders } from '../lib/queries';
import { can, useSession } from '../lib/session';
import {
  Alert, Amount, Button, Card, EmptyState, Pill, SectionTitle, Skeleton, Stat, TierBadge,
} from '../components/ui';
import { CustomerAccess } from '../components/CustomerAccess';
import { CustomerDelete } from '../components/CustomerDelete';
import { CustomerMerge } from '../components/CustomerMerge';
import { WalletIssue } from '../components/WalletIssue';
import { LoyaltyAdjust } from '../components/LoyaltyAdjust';
import { VehicleManager } from '../components/VehicleManager';

export function CustomerPage() {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const { data: session } = useSession();
  const { data: customer, isPending, error } = useCustomer(id);
  const { data: orders } = useCustomerOrders(id);
  const { data: authMethods } = useAuthMethods();

  if (isPending) {
    return (
      <div className="mx-auto max-w-6xl space-y-4">
        <Skeleton className="h-20" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="mx-auto max-w-2xl">
        <Alert>{t('customer.couldNotLoad')}</Alert>
      </div>
    );
  }

  const { loyalty } = customer;

  return (
    <div className="mx-auto max-w-6xl">
      <Link to="/customers" className="-my-2 inline-flex min-h-11 items-center text-xs text-ink-faint transition hover:text-ink-dim">
        {t('customer.back')}
      </Link>

      <header className="mt-3 mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-[2rem] font-bold leading-tight tracking-tight text-ink">{customer.name}</h1>
            <TierBadge code={loyalty.tier.code} />
            {loyalty.tier.heldByPermanence && <Pill>{t('customer.heldPermanently')}</Pill>}
            {customer.status === 'BLOCKED' && <Pill tone="danger">{t('customer.blockedPill')}</Pill>}
          </div>
          <p className="tabular mt-1.5 text-sm text-ink-dim">
            {customer.phone} · {customer.loyaltyId}
            {customer.email && ` · ${customer.email}`}
          </p>
        </div>

        {customer.status !== 'BLOCKED' && (
          <div className="flex flex-wrap gap-2">
            {can(session, 'orders.accept') && (
              <Link to={`/floor/accept?customer=${customer.id}`}>
                <Button variant="ghost">{t('floor.accept')}</Button>
              </Link>
            )}
            {can(session, 'orders.create') && (
              <Link to={`/orders/new?customer=${customer.id}`}>
                <Button>{t('customer.newOrder')}</Button>
              </Link>
            )}
          </div>
        )}
      </header>

      {customer.status === 'BLOCKED' && (
        <div className="mb-6">
          <Alert tone="warning">
            {t('customer.blockedWarning')}
            {customer.blockedReason && <span className="mt-1 block text-xs">{customer.blockedReason}</span>}
          </Alert>
        </div>
      )}

      <section className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label={t('customer.lifetimeSpend')}
          value={<Amount value={formatGel(loyalty.lifetimeSpendTetri)} />}
          sub={loyalty.nextTier ? (
            <span className="flex flex-col gap-1.5">
              <span>
                {t('customer.toTier', {
                  amount: formatGel(loyalty.nextTier.remainingTetri),
                  tier: loyalty.nextTier.code,
                })}
              </span>
              <span className="h-1 overflow-hidden rounded-full bg-muted">
                <span
                  className="block h-full rounded-full bg-brand/70"
                  style={{ width: `${Math.min(100, Math.max(2, Math.round(
                    (loyalty.lifetimeSpendTetri /
                      (loyalty.lifetimeSpendTetri + loyalty.nextTier.remainingTetri)) * 100,
                  )))}%` }}
                />
              </span>
            </span>
          ) : t('customer.topTierOf', { code: loyalty.tier.code })}
        />
        <Stat
          label={t('customer.cashback')}
          value={<Amount
            value={formatGel(loyalty.cashbackAvailableTetri)}
            className="text-brand"
            symbolClassName="text-brand/60"
          />}
          sub={loyalty.cashbackPendingTetri > 0
            ? t('customer.maturing', { amount: formatGel(loyalty.cashbackPendingTetri) })
            : t('customer.earnsAt', { rate: formatRate(loyalty.tier.cashbackRateBp) })}
        />
        <Stat
          label={t('customer.points')}
          value={formatPoints(loyalty.pointsBalance)}
          sub={t('customer.perLari', {
            multiplier: formatMultiplier(loyalty.tier.pointsMultiplierMilli),
          })}
        />
        <Stat
          label={t('customer.orders')}
          value={loyalty.completedOrders}
          sub={loyalty.completedOrders > 0
            ? t('customer.avg', { amount: formatGel(loyalty.averageOrderTetri) })
            : t('customer.noVisits')}
        />
      </section>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="min-w-0 lg:col-span-2">
          <SectionTitle>{t('customer.visits')}</SectionTitle>
          {!orders?.items.length && (
            <EmptyState title={t('customer.noVisits')} hint={t('customer.visitsEmpty')} />
          )}
          <div className="space-y-2">
            {orders?.items.map((order) => {
              const vehicle = customer.vehicles.find((v) => v.id === order.vehicleId);
              const refunded = order.status === 'REFUNDED' || order.status === 'PARTIALLY_REFUNDED';
              return (
                <Card key={order.id} className={refunded ? 'opacity-70' : ''}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="tabular text-sm font-semibold text-ink">{order.reference}</span>
                        {refunded && (
                          <Pill tone="warning">
                            {t(order.status === 'REFUNDED'
                              ? 'statusShort.REFUNDED'
                              : 'statusShort.PARTIALLY_REFUNDED')}
                          </Pill>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-ink-faint">
                        {order.completedAt ? formatDateTime(order.completedAt) : t('order.notCompleted')}
                        {vehicle && ` · ${vehicle.make} ${vehicle.model}`}
                      </div>
                    </div>
                    <div className="text-right">
                      <Amount
                        value={formatGel(order.orderValueTetri)}
                        className="block text-sm font-semibold text-ink"
                      />
                      <div className="text-[0.7rem] text-ink-faint">
                        {order.cashbackUsedTetri > 0 && t('customer.cashbackSpentLine', {
                          amount: formatGel(order.cashbackUsedTetri),
                        })}
                        +{formatGel(order.cashbackEarnedTetri)} · +{formatPoints(order.pointsEarned)}{' '}
                        {t('common.pts')}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>

        <aside className="min-w-0 space-y-6">
          <VehicleManager
            customerId={customer.id}
            vehicles={customer.vehicles}
            canEdit={can(session, 'vehicles.create')}
          />

          {(customer.referral.code || customer.referral.referredBy) && (
            <Card>
              <SectionTitle>{t('customer.referrals')}</SectionTitle>
              <dl className="space-y-2 text-sm">
                {customer.referral.code && (
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-ink-dim">{t('customer.theirCode')}</dt>
                    <dd className="tabular font-semibold tracking-[0.15em] text-brand">
                      {customer.referral.code}
                    </dd>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-ink-dim">{t('customer.broughtPeople')}</dt>
                  <dd className="tabular text-ink">{customer.referral.invited}</dd>
                </div>
                {customer.referral.referredBy && (
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-ink-dim">{t('customer.invitedBy')}</dt>
                    <dd>
                      <Link
                        to={`/customers/${customer.referral.referredBy.id}`}
                        className="font-medium text-ink hover:text-brand"
                      >
                        {customer.referral.referredBy.name}
                      </Link>
                      {!customer.referral.rewardedAt && (
                        <span className="ml-2 text-[0.68rem] text-ink-faint">
                          {t('customer.bonusNotPaid')}
                        </span>
                      )}
                    </dd>
                  </div>
                )}
              </dl>
            </Card>
          )}

          {can(session, 'wallet.read') && customer.status !== 'MERGED' && (
            <WalletIssue customerId={customer.id} />
          )}

          {can(session, 'customers.delete') && (
            <CustomerDelete
              customerId={customer.id}
              loyaltyId={customer.loyaltyId}
              name={customer.name}
            />
          )}

          {can(session, 'customers.merge') && customer.status !== 'MERGED' && (
            <CustomerMerge targetId={customer.id} targetName={customer.firstName} />
          )}

          {can(session, 'loyalty.adjust') && (
            <LoyaltyAdjust
              customerId={customer.id}
              cashbackTetri={loyalty.cashbackAvailableTetri}
              pointsBalance={loyalty.pointsBalance}
              lifetimeSpendTetri={loyalty.lifetimeSpendTetri}
            />
          )}

          {can(session, 'customers.update') && (
            <CustomerAccess
              customerId={customer.id}
              otpAvailable={authMethods?.otp.available ?? false}
            />
          )}

          {customer.notes.length > 0 && (
            <section>
              <SectionTitle>{t('customer.notes')}</SectionTitle>
              <div className="space-y-2">
                {customer.notes.map((note) => (
                  <Card key={note.id}>
                    <p className="text-sm text-ink-dim">{note.content}</p>
                    <p className="mt-1.5 text-[0.7rem] text-ink-faint">{formatDate(note.createdAt)}</p>
                  </Card>
                ))}
              </div>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
